import { sanitizeFileName } from "@intentsify/utils";
import { ErrorCode, FileRejection } from "react-dropzone";

export const getUploadStatus = ({
  progress,
  isUploading,
  isLoadingValidationResults,
  hasValidationResults,
  shouldFetchValidation,
}: {
  progress: {
    progress: number;
    secondsRemaining: number;
    isFinalizing: boolean;
  };
  isUploading: boolean;
  isLoadingValidationResults: boolean;
  hasValidationResults: boolean;
  shouldFetchValidation: boolean;
}) => {
  switch (true) {
    case isLoadingValidationResults:
      return {
        status: "Fetching validation results...",
      };
    case !progress.isFinalizing && isUploading:
      return {
        status: "Uploading file...",
        statusSubtitle:
          isNaN(progress.secondsRemaining) || progress.secondsRemaining <= 0
            ? undefined
            : `About ${Math.round(
                progress.secondsRemaining,
              )} seconds remaining.`,
      };

    case progress.isFinalizing:
      return {
        status: "Saving file...",
      };
    case shouldFetchValidation && !hasValidationResults:
      return {
        status: "Validating file...",
      };
  }
};

export const getUniqueFileName = (
  fileName: string,
  usedFileNames: string[],
) => {
  const sanitizedFileName = `${sanitizeFileName(fileName.slice(0, -4))}.csv`;

  if (!usedFileNames.includes(sanitizedFileName)) {
    return sanitizedFileName;
  }

  const getName = (number: number): string => {
    const fileNameWithoutExtension = fileName.slice(0, -4);
    const newFileName = `${sanitizeFileName(
      fileNameWithoutExtension,
    )}(${number}).csv`;

    if (!usedFileNames.includes(newFileName)) {
      return newFileName;
    }

    return getName(number + 1);
  };

  return getName(1);
};

export const MAX_FILE_SIZE_MB = 200;
export const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

export function getRejectionMessage(rejections: FileRejection[]): string {
  const codes = new Set(
    rejections.flatMap((rejection) => rejection.errors.map((e) => e.code)),
  );

  if (codes.has(ErrorCode.TooManyFiles)) {
    return "Please upload a single CSV file.";
  }

  if (codes.has(ErrorCode.FileInvalidType)) {
    return "Only CSV files are supported.";
  }

  if (codes.has(ErrorCode.FileTooLarge)) {
    return `File is too large. Maximum size allowed: ${MAX_FILE_SIZE_MB}MB.`;
  }

  return rejections[0]?.errors[0]?.message ?? "Please upload a valid CSV file.";
}
